import { useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import Cookies from "js-cookie";
import AutoPlaces from "../../Geosearch/AutoPlaces";
import { getConfig, getFlagUrl, regionConfigs } from "../../../../../custom-config/region.configs";
import { submitPardotFormHandler } from "~/lib/utils/PardotUtils/index";
import { Separator } from "~/components/ui/separator";
import ListingsService from "~/lib/services/ListingsService";
import {
  fireConversionEvent,
  LEAD_FORMS,
  LEAD_STATUS,
  trackLead,
  trackQuoteRequestConversion,
  trackEmailCaptured,
  trackFormOpen,
  trackFormStep,
  DATA_LAYER_EVENTS,
} from "~/lib/ConversionTracking";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { getItem, removeItem } from "~/utils/storage";
import { getAdClickIds } from "~/lib/helpers/adClickIds";
import { QuoteRequestFlowModalFormHandler } from "~/lib/helpers/regionPardotFormHelper";
import { toQueryString } from "~/lib/helpers/StringHelper";
import { useFibonacciFormStore } from "~/lib/hooks/useSessionTracker";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, } from "~/components/ui/custom-bottom-drawer";
import { Lightbulb, X } from "lucide-react";
import EmailService from "~/lib/services/EmailService";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { geocodeByAddress } from "react-places-autocomplete";
import { getLocationVars } from "~/lib/helpers/googleLocationDataManager";
import { loadGoogleMapsScript } from "~/utils/loadGoogleMaps";
import {
  getSearchPathSlugs,
  type IpLocationForSearchPath,
} from "~/lib/helpers/ipLocationSearchPathHelper";
import { convertSearchUrlToLocationPage, buildCountryPropertyPagePath, buildPropertyNavCityUrl } from "~/components/blocks/Geosearch/locationGeosearch";
import { Link } from "@remix-run/react";

// ------------------ Constants ------------------
const MAXIMUM_POST_RETRIES = 3;

const WORK_EMAIL_HINT_MESSAGES = [
  "Work emails often unlock enterprise-level support faster.",
  "Work emails help sales teams prioritize inquiries faster.",
  "Professional emails reduce chances of spam filtering.",
  "Business emails are less likely to be ignored.",
  "Work emails often get faster business responses.",
] as const;

function pickRandomWorkEmailHint(): string {
  return WORK_EMAIL_HINT_MESSAGES[
    Math.floor(Math.random() * WORK_EMAIL_HINT_MESSAGES.length)
  ]!;
}

async function fetchCurrentLocationClient(): Promise<IpLocationForSearchPath | null> {
  try {
    const response = await fetch("/api/location");
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function stripMinPriceZero(searchUrl: string): string {
  // Ensure `minPrice=0` isn't added to URLs.
  try {
    const base =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const u = new URL(searchUrl, base);
    if (u.searchParams.get("minPrice") === "0") {
      u.searchParams.delete("minPrice");
      const qs = u.searchParams.toString();
      return `${u.pathname}${qs ? `?${qs}` : ""}`;
    }
  } catch {
    // If parsing fails, return original string.
  }
  return searchUrl;
}

function buildGeoSearchUrlFromIpLocation(
  location: IpLocationForSearchPath,
  minDesk: number,
  budget: number,
): string {
  const countryCode =
    location.countryCode?.toLowerCase() === "gb"
      ? "uk"
      : location.countryCode?.toLowerCase() || "us";
  const { stateSlug, citySlug } = getSearchPathSlugs(location);
  const budgetParam = budget > 0 ? budget : 0;
  const query = {
    ...(location.lat != null && location.lon != null
      ? { latitude: location.lat, longitude: location.lon, zoom: 15 }
      : {}),
    minPeople: minDesk,
    maxPrice: budgetParam,
  };

  if (!stateSlug || !citySlug) {
    const params = new URLSearchParams({
      minPeople: String(minDesk),
      maxPrice: String(budgetParam),
    });
    return `${buildCountryPropertyPagePath(countryCode)}?${params.toString()}`;
  }

  return buildPropertyNavCityUrl(`/${countryCode}`, stateSlug, citySlug, query);
}

async function resolveSearchRedirectUrl(
  geoSearch: string,
  region: string,
  minDesk: number,
  budget: number,
): Promise<string> {
  // AutoPlaces / stored values may still be legacy /search URLs.
  if (geoSearch) return convertSearchUrlToLocationPage(stripMinPriceZero(geoSearch));

  const location = await fetchCurrentLocationClient();
  if (location) {
    return buildGeoSearchUrlFromIpLocation(location, minDesk, budget);
  }

  const regionCode = region === "uk" ? "uk" : region;
  const budgetParam = budget > 0 ? budget : 0;
  const params = new URLSearchParams({
    minPeople: String(minDesk),
    maxPrice: String(budgetParam),
  });
  return `${buildCountryPropertyPagePath(regionCode)}?${params.toString()}`;
}

const officeSizeOptions = [
  { label: "Just me", minDesk: 1, maxDesk: 1 },
  { label: "2 - 3", minDesk: 2, maxDesk: 3 },
  { label: "4 - 6", minDesk: 4, maxDesk: 6 },
  { label: "7 - 10", minDesk: 7, maxDesk: 10 },
  { label: "11 - 20", minDesk: 11, maxDesk: 20 },
  { label: "21 - 30", minDesk: 21, maxDesk: 30 },
  { label: "31 - 50", minDesk: 31, maxDesk: 50 },
  { label: "51 - 100", minDesk: 51, maxDesk: 100 },
  { label: "More than 100", minDesk: 100, maxDesk: 200 },
];

const durationOptions = [
  "Less than a month",
  "Month-to-month",
  "2-6 months",
  "6-12 months",
  "12-24 months",
];

const moveinOptions = [
  "ASAP",
  "Within the 2 weeks",
  "Within the next month",
  "Within the 1-2 months",
  "Within the 2-3 months",
  "Others",
];

const OptionRadioGroup = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (val: string) => void;
}) => (
  <RadioGroup value={value} onValueChange={onChange} className="border rounded-lg gap-0">
    {options.map((option) => (
      <div
        key={option}
        className={`flex border border-[#eee] items-center space-x-2 px-4 py-4 ${value === option ? "bg-gray-100" : ""
          }`}
      >
        <RadioGroupItem value={option} id={option} />
        <Label htmlFor={option}>{option}</Label>
      </div>
    ))}
  </RadioGroup>
);

// ------------------ Component ------------------
type UniqueVisitorModalProps = {
  visible: boolean;
  handleCancel: () => void;
  handleSubmit: () => void;
  region?: string;
  params?: Record<string, string | undefined>;
};

export function UniqueVisitorModal({
  visible,
  handleCancel,
  handleSubmit,
  region = "us",
  params,
}: UniqueVisitorModalProps) {
  const isClient = typeof window !== "undefined";
  const screenWidthFromCookie = isClient
    ? parseInt(Cookies.get("screenWidth") || "0", 10)
    : 1024;
  // Fallback to window.innerWidth when cookie not set yet (e.g. first load on real device)
  const screenWidth =
    isClient && screenWidthFromCookie === 0
      ? window.innerWidth
      : screenWidthFromCookie;
  const isMobile = screenWidth > 0 && screenWidth < 768;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ firstName: "", lastName: "", company: "", email: "" });
  const [payload, setPayload] = useState({
    officeType: "",
    peopleCount: "",
    duration: "",
    budget: 0, // store as number
    budgetCurrency: "",
    moveSchedule: "",
    location: "",
    locationLatLng: { lat: undefined as any, lng: undefined as any },
    locationCountry: "",
    locationState: "",
    locationStreet: "",
    locationCity: "",
    email: "",
    phone: "",
    phoneCountry: "",
    firstName: "",
    lastName: "",
    comments: "",
    company: "",
    formUrl: typeof window !== "undefined" ? window.location.href : "",
    userLandingUrl: typeof window !== "undefined" ? window.location.href : "",
    geoSearch: "",
    minDesk: 1,
    maxDesk: 1,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postErrorCount, setPostErrorCount] = useState(0);
  const [officeCount, setOfficeCount] = useState(1);

  const [emailError, setEmailError] = useState("");
  const [emailFreemailHint, setEmailFreemailHint] = useState<string | null>(null);
  const [isEmailChecking, setIsEmailChecking] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const emailVerifyRequestIdRef = useRef(0);
  const debouncedValidateEmailRef = useRef<ReturnType<typeof debounce> | null>(null);
  const [showingSuggestions, setShowingSuggestions] = useState(false);

  const { markCancelled, markCompleted } = useFibonacciFormStore()
  const updatePayload = (updates: Partial<typeof payload>) =>
    setPayload((prev) => ({ ...prev, ...updates }));

  const phoneDefaultCountry = (region === "uk" ? "gb" : region) as any;

  // Normalize phone to E.164-like string: +<digits only>
  const normalizePhone = (value: string, formatted?: string) => {
    const source = formatted || value || "";
    const digits = source.replace(/[^\d]/g, "");
    if (!digits) return "";
    return `+${digits}`;
  };

  const formatBudget = (value: number) =>
    value && !Number.isNaN(value) ? value.toLocaleString() : "";

  const updateForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      emailVerifyRequestIdRef.current += 1;
      setEmailError("");
      setEmailFreemailHint(null);
      setIsEmailValid(false);
      debouncedValidateEmailRef.current?.(value);
    }
  };

  useEffect(() => {
    (async () => {
      const userLandingUrl = await getItem("landingUrl");
      const campaignData = getItem<{
        keyword?: string;
        network?: string;
        device?: string;
        campaign?: string;
        location?: string;
        workspace?: string;
        country?: string;
      }>("campaignData");

      const updates: any = {
        userLandingUrl,
        budgetCurrency: regionConfigs[region as keyof typeof regionConfigs]?.currency || "USD",
        ...getAdClickIds(),
        device: campaignData?.device || params?.device || "",
        ppcKeywords: campaignData?.keyword || params?.keywords || "",
        campaign: campaignData?.campaign || params?.campaign || "",
      };

      // Only update location-related fields if campaignData has them
      if (campaignData?.location) {
        updates.location = campaignData.location;
      }
      if (campaignData?.country) {
        updates.locationCountry = campaignData.country;
      }
      if (campaignData?.workspace) {
        updates.officeType = campaignData.workspace;
      }

      updatePayload(updates);
    })();
  }, [region, params]);


  // ------------------ Email Validation ------------------
  if (!debouncedValidateEmailRef.current) {
    debouncedValidateEmailRef.current = debounce(async (email: string) => {
      const trimmed = email.trim();
      if (!trimmed) {
        setEmailFreemailHint(null);
        setEmailError("");
        setIsEmailValid(false);
        setIsEmailChecking(false);
        return;
      }

      const requestId = ++emailVerifyRequestIdRef.current;
      setIsEmailChecking(true);
      setEmailError("");
      setEmailFreemailHint(null);
      setIsEmailValid(false);

      try {
        const data = await EmailService.verifyEmail(
          encodeURIComponent(trimmed),
        );
        if (requestId !== emailVerifyRequestIdRef.current) return;

        const payload = (data as any)?.data ?? data;
        const isFreemailRaw =
          (payload as any)?.["is-freemail"] ??
          (payload as any)?.isFreemail ??
          (payload as any)?.["is_freemail"];
        const isFreemail =
          typeof isFreemailRaw === "string"
            ? isFreemailRaw.toLowerCase() === "true"
            : Boolean(isFreemailRaw);

        if (isFreemail) {
          setEmailFreemailHint(pickRandomWorkEmailHint());
          setEmailError("");
          setIsEmailValid(true);
          return;
        }

        const isVerified =
          Boolean((payload as any)?.verified) ||
          Boolean((payload as any)?.valid);
        if (!isVerified) {
          setEmailFreemailHint(null);
          setEmailError("Please enter a valid email address");
          setIsEmailValid(false);
          return;
        }
        setEmailFreemailHint(null);
        setIsEmailValid(true);
      } catch {
        if (requestId !== emailVerifyRequestIdRef.current) return;
        setEmailFreemailHint(null);
        setEmailError("Could not verify email. Try again.");
        setIsEmailValid(false);
      } finally {
        if (requestId === emailVerifyRequestIdRef.current) {
          setIsEmailChecking(false);
        }
      }
    }, 400);
  }

  useEffect(() => {
    return () => debouncedValidateEmailRef.current?.cancel();
  }, []);

  useEffect(() => {
    if (step !== 6) return;
    const trimmed = form.email.trim();
    if (!trimmed) {
      setIsEmailValid(false);
      return;
    }
    debouncedValidateEmailRef.current?.(form.email);
  }, [step]);

  // ------------------ Submission ------------------
  const sendToPardot = ({ isComplete = false }, callback?: () => void) => {
    // Get campaign data from localStorage if available
    const campaignData = getItem<{
      keyword?: string;
      network?: string;
      device?: string;
      campaign?: string;
      location?: string;
      workspace?: string;
      country?: string;
    }>("campaignData");

    const comments = `
      Comments: ${payload.comments}
      | First Name: ${form.firstName}
      | Last Name: ${form.lastName}
      | Company: ${form.company}
      | Email: ${form.email}
      | Phone: ${payload.phone}
      | Location: ${payload.location}
      | State: ${payload.locationState}
      | Street: ${payload.locationStreet}
      | Lat lng: ${payload.locationLatLng.lat}, ${payload.locationLatLng.lng}
      | Number of desks: ${payload.peopleCount}
      | Duration: ${payload.duration}
      | Budget: ${payload.budget || 0} ${payload.budgetCurrency}
      | Move schedule: ${payload.moveSchedule}
    `;

    const pardotPayload = {
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      company: form.company,
      phone: payload.phone,
      minPeople: String(payload.minDesk),
      maxPeople: String(payload.maxDesk),
      officeType: payload.officeType,
      duration: payload.duration,
      maxBudget: String(payload.budget || 0),
      location: payload.location,
      locationCity: payload.locationCity,
      locationCountry: payload.locationCountry,
      locationState: payload.locationState,
      locationStreet: payload.locationStreet,
      comments,
      formUrl: payload.formUrl,
      userLandingUrl: payload.userLandingUrl,
      // Prefer phone dropdown country over URL-derived region/country
      country: payload.phoneCountry || payload.locationCountry,
      ppcKeywords: campaignData?.keyword || (payload as any).ppcKeywords || (payload as any).userKeywords || "",
      campaign: campaignData?.campaign || (payload as any).campaign || "",
      device: campaignData?.device || (payload as any).device || "",
      network: campaignData?.network || "",
      workspace: campaignData?.workspace || payload.officeType || "",
      leadStatus: "New",
    };

    submitPardotFormHandler(
      QuoteRequestFlowModalFormHandler(region, isComplete),
      pardotPayload,
      () => ListingsService.sendErrorNotification({ ...payload, ...form }, "quote-request"),
      callback
    );
  };

  const postPayload = async () => {
    if (postErrorCount >= MAXIMUM_POST_RETRIES || isSubmitting) return;
    setIsSubmitting(true);

    sendToPardot({ isComplete: true }, async () => {
      // Pardot's no-cors request has been dispatched. This is more reliable
      // than observing the confirmation heading, although only CRM field
      // mapping can prove that every submitted field was persisted.
      trackLead({
        form: LEAD_FORMS.QUOTE_POPUP,
        status: LEAD_STATUS.COMPLETE,
        userData: { email: form.email, phone_number: payload.phone },
      });
      try {
        await ListingsService.sendSuccessNotification({ ...payload, ...form }, "quote-request");
      } catch {
        setPostErrorCount((c) => c + 1);
      }
    });

    setIsSubmitting(false);
    trackQuoteRequestConversion({
      email: form.email,
      phone_number: payload.phone,
    });

    // Remove ppcKeywords from localStorage after submission
    removeItem("ppcKeywords");

    const countQuery = toQueryString({
      latitude: payload.locationLatLng.lat,
      longitude: payload.locationLatLng.lng,
      radius: 10000,
      minPeople: payload.minDesk,
      maxPeople: payload.maxDesk,
      // Location fallback for when the pick had no coords — the backend counts
      // by country + the deepest of these (street > city > state).
      country: payload.locationCountry,
      state: payload.locationState,
      city: payload.locationCity,
      street: payload.locationStreet,
    });
    try {
      const count = await ListingsService.getAvailableListingsCount(countQuery);
      setOfficeCount(count.available_space_count);
    } catch (err) {
      // The lead is already submitted above; a failed count (e.g. missing
      // coords) must NOT block reaching the confirmation step.
      console.error("Available-listings count failed", err);
    }

    // Remove campaignData from localStorage after successful submission
    removeItem("campaignData");
    setStep(9);
  };

  // The modal only renders when it is shown, so mounting is the open. One
  // effect on `step` then covers every step — including any added later, which
  // is why no Continue button needs touching.
  useEffect(() => {
    trackFormOpen(LEAD_FORMS.QUOTE_POPUP);
  }, []);

  useEffect(() => {
    trackFormStep({ form: LEAD_FORMS.QUOTE_POPUP, step });
  }, [step]);

  // Step 6 -> 7. The Next button is disabled until EmailService has verified the
  // address, so reaching here means we hold a usable email even if the visitor
  // abandons the rest of the form. Keyed on the value so correcting a typo fires
  // again, while Back/Next with the same address does not.
  const capturedEmailRef = useRef("");
  const handleEmailStepNext = () => {
    if (form.email && form.email !== capturedEmailRef.current) {
      capturedEmailRef.current = form.email;
      trackEmailCaptured({ form: LEAD_FORMS.QUOTE_POPUP, email: form.email });
    }
    setStep(7);
  };

  const handleIncompleteClose = () => {
    if (step > 5 && form.email) {
      sendToPardot({ isComplete: false }, async () => {
        trackLead({
          form: LEAD_FORMS.QUOTE_POPUP,
          status: LEAD_STATUS.PARTIAL,
          userData: { email: form.email, phone_number: payload.phone },
        });
        try {
          await ListingsService.sendSuccessNotification({ ...payload, ...form }, "quote-request");
        } catch {
          // The lead may still have reached Pardot; nothing to retry here.
        }
      });
      trackQuoteRequestConversion();
    }
  };

  const redirectToSearchOnClose = async () => {
    const searchUrl = await resolveSearchRedirectUrl(
      payload.geoSearch,
      region,
      payload.minDesk,
      payload.budget,
    );
    if (!searchUrl) return;

    const url = searchUrl.startsWith("/") ? searchUrl : `/${searchUrl}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleModalClose = (open: boolean) => {
    if (open) return;

    if (step > 3 && step < 9) {
      void redirectToSearchOnClose();
    }

    if (step === 9) {
      markCompleted();
      return;
    }

    handleIncompleteClose();
    handleCancel();
  };

  // ------------------ UI ------------------
  return (
    <>
      {isMobile ? (
        <Drawer
          open={visible}
          onOpenChange={handleModalClose}
        >
          <DrawerContent
            className={`transition-all duration-200
                ${step === 1
                ? "h-auto max-h-[85vh]"
                : step === 2
                  ? "h-auto max-h-[60vh]"
                  : step === 3
                    ? "h-[25vh] max-h-[25vh]"
                    : step === 4
                      ? "h-auto max-h-[65vh]"
                      : step === 5
                        ? showingSuggestions ? "h-[45vh] max-h-[45vh]" : "h-[25vh] max-h-[25vh]"
                        : step === 6
                          ? "h-[75vh] max-h-[75vh]"
                          : step === 7
                            ? "h-[75vh] max-h-[75vh]"
                            : step === 8
                              ? "h-[75vh] max-h-[75vh]"
                              : step === 9
                                ? "h-auto max-h-[85vh]"
                                : "h-[85vh] max-h-[85vh]"
              } overflow-visible`}
          >
            <div className="absolute top-0  w-full bg-gray-200 rounded-full h-3 z-50">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${step === 9 ? "bg-oh-primary" : "bg-oh-primary"
                  }`}
                style={{ width: `${step === 9 ? 100 : (step / 8) * 100}%` }}
              />
            </div>
            {step === 1 && (
              <>
                <DrawerHeader className="px-3">
                  <div className="flex items-center justify-between">

                    <p className="text-sm font-semibold pt-4 flex-1">
                      How many people do you need office space for?
                    </p>

                  </div>
                </DrawerHeader>
                <Separator className="mb-6" />

                <div className="px-4 md:px-0">
                  <RadioGroup
                    value={payload.peopleCount} // use label as stable key
                    onValueChange={(label) => {
                      console.log(label);
                      const chosen = officeSizeOptions.find((o) => o.label === label)!;
                      updatePayload({
                        peopleCount: chosen.label,
                        minDesk: chosen.minDesk,
                        maxDesk: chosen.maxDesk,
                      });

                    }}
                    onClick={() => {
                      fireConversionEvent(DATA_LAYER_EVENTS.QUOTE_REQUEST_MODAL_CONTINUE);

                    }}
                    className="border rounded-lg gap-0"
                  >
                    {officeSizeOptions.map((option) => (
                      <div
                        key={option.label}
                        className={`flex border border-[#eee] items-center space-x-2 px-4 py-4 ${payload.peopleCount === option.label ? "bg-gray-100" : ""
                          }`}
                      >
                        <RadioGroupItem value={option.label} id={option.label} />
                        <Label htmlFor={option.label}>{option.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="mt-6 flex justify-end p-4 md:p-0">
                  <OhButton
                    disabled={!payload.peopleCount}
                    onClick={() => {
                      fireConversionEvent(DATA_LAYER_EVENTS.QUOTE_REQUEST_MODAL_CONTINUE);
                      setStep(2);
                    }}
                  >
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 2: Duration */}
            {step === 2 && (
              <>
                <DrawerHeader className="px-4">
                  <div className="flex items-center justify-between">

                    <h2 className="text-sm font-semibold pt-4 flex-1">
                      How long are you looking to rent for?
                    </h2>
                  </div>
                </DrawerHeader>
                <Separator className="mb-6" />
                <div className="px-4 md:px-0">
                  <OptionRadioGroup
                    value={payload.duration}
                    options={durationOptions}
                    onChange={(val) => {
                      updatePayload({ duration: val });
                      setStep(3)
                    }}
                  />
                </div>

                <div className="mt-6 flex justify-between p-4 md:p-0">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <OhButton onClick={() => setStep(3)}>
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 3: Budget */}
            {step === 3 && (
              <>
                <DrawerHeader className="px-4">
                  <div className="flex items-center justify-between">

                    <h2 className="text-sm font-semibold pt-4 flex-1">
                      What's your budget?
                    </h2>

                  </div>
                </DrawerHeader>
                <Separator className="mb-6" />
                <div className="relative flex px-4 md:px-0">
                  <Select
                    value={payload.budgetCurrency}
                    onValueChange={(val) => updatePayload({ budgetCurrency: val })}
                  >
                    <SelectTrigger className="w-28 rounded-tr-none rounded-br-none bg-gray-200 focus:ring-transparent">
                      <SelectValue placeholder={payload.budgetCurrency} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(regionConfigs)
                        .filter(([_, cfg]: [string, any]) => (cfg as any).top || cfg.region === region)
                        .sort(([_, a]: any, [__, b]: any) => {
                          if (a.region === region) return -1;
                          if (b.region === region) return 1;
                          return 0;
                        })
                        .map(([key, cfg]: [string, any]) => (
                          <SelectItem key={key} value={cfg.currency} className="pl-2 pr-8 [&>span]:left-auto [&>span]:right-2">
                            <div className="flex items-center gap-2">
                              <img
                                src={getFlagUrl(cfg.region)}
                                alt={cfg.country}
                                className="w-5 h-5 object-cover rounded-sm shrink-0"
                              />
                              <span>{cfg.currency}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="text" // use text to show formatted value
                    placeholder="Please enter your monthly budget"
                    className="rounded-tl-none rounded-bl-none"
                    value={formatBudget(payload.budget)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      const num = Number(raw);
                      if (!isNaN(num)) {
                        updatePayload({ budget: num });
                      }
                    }}
                  />
                </div>
                <div className="my-6 px-4 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <OhButton disabled={!payload.budget || payload.budget <= 0} onClick={() => setStep(4)}>
                    Next
                  </OhButton>
                </div>
              </>
            )}


            {/* Step 4: Move-in */}
            {step === 4 && (
              <>
                <DrawerHeader className="px-4">
                  <div className="flex items-center justify-between">

                    <h2 className="text-sm font-semibold pt-4 flex-1">
                      How soon are you looking to move in?
                    </h2>

                  </div>
                </DrawerHeader>
                <Separator className="mb-6" />
                <div className="px-4 md:px-0">
                  <OptionRadioGroup
                    value={payload.moveSchedule}
                    options={moveinOptions}
                    onChange={(val) => {
                      updatePayload({ moveSchedule: val });
                      setStep(5)
                    }}
                  />
                </div>

                <div className="mt-6 flex justify-between p-4 md:p-0">
                  <Button variant="outline" onClick={() => setStep(3)}>
                    Back
                  </Button>
                  <OhButton onClick={() => setStep(5)}>
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 5: Location */}
            {step === 5 && (
              <div className="flex flex-col h-full px-4">
                <div className="flex items-center justify-between mb-4 mt-2">

                  <h2 className="text-sm font-semibold pt-4 flex-1">
                    Where do you need Office Space?
                  </h2>
                  <DrawerClose asChild>
                    <button
                      className="text-gray-500 hover:text-black transition-colors p-1"
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </DrawerClose>

                </div>
                <div className={`overflow-visible flex-1 transition-all duration-200 ${showingSuggestions ? "min-h-[400px]" : ""
                  }`}>
                  <AutoPlaces
                    mobile={isMobile}
                    inModal={true}
                    value={payload.location}
                    onSuggestionsVisibilityChange={setShowingSuggestions}
                    onLocationSelect={async (val: string, location: string) => {
                      if (!val || !location) {
                        updatePayload({
                          geoSearch: "",
                          location: "",
                          locationCountry: "",
                          locationState: "",
                          locationStreet: "",
                          locationCity: "",
                          locationLatLng: { lat: undefined, lng: undefined }
                        });
                        return;
                      }

                      const data: any = { geoSearch: "", locationCountry: "", locationState: "", locationStreet: "" };
                      const [base, query] = val.split("?");
                      // Only the min-desk (minPeople) filter carries over to the
                      // location page URL — budget/other filters are left off.
                      const withFilters = val.includes("?")
                        ? `${val}&minPeople=${payload.minDesk}`
                        : `${val}?minPeople=${payload.minDesk}`;
                      data.geoSearch = convertSearchUrlToLocationPage(withFilters);

                      const segments = base.split("/").filter(Boolean);
                      const regionCode = segments[0] === "search" ? "us" : segments[0];
                      const locationSegments =
                        segments[0] === "search" ? segments.slice(1) : segments.slice(1);

                      // Every pick (country / state / city+) activates Next: geoSearch
                      // is already set above, and coord-less picks get their lat/lng
                      // from the geocode fallback below. URLSearchParams safely parses
                      // lat/lng when the URL carries a query string (guarded so an
                      // undefined query never crashes).
                      const partsQ = new URLSearchParams(query || "");
                      let lat: any = partsQ.get("latitude") || undefined;
                      let lng: any = partsQ.get("longitude") || undefined;

                      const config = getConfig(regionCode as any) as any;
                      data.locationCountry = config?.country || "";
                      data.region = regionCode;
                      if (
                        locationSegments.length >= 2 &&
                        !locationSegments[0]?.startsWith("office-space")
                      ) {
                        data.locationCity = locationSegments[locationSegments.length - 1];
                      }

                      try {
                        // Some pages don't preload Google Maps JS — load it before
                        // geocoding, otherwise geocodeByAddress throws "Cannot read
                        // properties of undefined (reading 'maps')". Resolves
                        // immediately when Maps is already present; skips cleanly
                        // if it can't load (Next still works via geoSearch, and the
                        // count API falls back to country/state/city).
                        const mapsReady = await new Promise<boolean>((resolve) =>
                          loadGoogleMapsScript(resolve),
                        );
                        if (mapsReady && (window as any).google?.maps) {
                          const geocodeResults = await geocodeByAddress(location);
                          const { state_long, state_short, streetName, city, country } = getLocationVars(geocodeResults as any);

                          data.locationState = state_long || state_short || "";
                          data.locationStreet = streetName || "";
                          if (!data.locationCity && city) data.locationCity = city;
                          if (!data.locationCountry && country) data.locationCountry = country;

                          // An area/city pick's URL carries no coords, but the
                          // submit's available-listings-count API requires
                          // lat/lng — fall back to the geocode result's point.
                          const point = (geocodeResults as any)?.[0]?.geometry?.location;
                          if ((lat == null || lng == null) && point) {
                            lat = typeof point.lat === "function" ? point.lat() : point.lat;
                            lng = typeof point.lng === "function" ? point.lng() : point.lng;
                          }
                        }
                      } catch (err) {
                        console.error("Error resolving location details", err);
                      }

                      // Only STREET/CITY picks carry a meaningful point. A
                      // COUNTRY/STATE pick's geocode returns the region centroid
                      // (e.g. Australia's desert middle), so a radius count around
                      // it returns 0 — omit lat/lng for those and let the submit's
                      // count API fall back to country/state/city.
                      const isCityOrStreet = !!data.locationCity || !!data.locationStreet;
                      updatePayload({
                        ...data,
                        location,
                        locationLatLng: isCityOrStreet
                          ? { lat, lng }
                          : { lat: undefined, lng: undefined },
                      });
                    }}
                  />
                </div>
                <div className="mt-6 flex justify-between pb-4">
                  <Button variant="outline" onClick={() => setStep(4)}>
                    Back
                  </Button>
                  <Button className="bg-oh-primary" disabled={!payload.geoSearch} onClick={() => setStep(6)}>
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Step 6: Email */}
            {step === 6 && (
              <>
                <DrawerHeader className="px-4">
                  <div className="flex items-center justify-between">

                    <h2 className="text-sm font-semibold pt-4 flex-1">
                      What email address should we send quotes to?
                    </h2>

                  </div>
                </DrawerHeader>
                <Separator className="mb-6" />
                <div className="px-4 md:px-0">
                  <Input
                    placeholder="Please enter your email"
                    name="email"
                    value={form.email}
                    onChange={updateForm}
                  />
                  {emailError && (
                    <p className="text-red-500 text-sm mt-1">{emailError}</p>
                  )}
                  {emailFreemailHint && (
                    <div className="mt-1 flex items-start gap-1.5 text-sm text-green-600">
                      <Lightbulb
                        className="mt-0.5 h-4 w-4 shrink-0 fill-yellow-400 stroke-black stroke-2"
                        aria-hidden
                      />
                      <span>{emailFreemailHint}</span>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-between p-4 md:p-4">
                  <Button variant="outline" onClick={() => setStep(5)}>
                    Back
                  </Button>
                  <OhButton
                    disabled={
                      !form.email || !isEmailValid || isEmailChecking
                    }
                    onClick={handleEmailStepNext}
                  >
                    {isEmailChecking ? "Checking..." : "Next"}
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 7: Name + Company */}
            {step === 7 && (
              <>
                <DrawerHeader className="px-4">
                  <div className="flex items-center justify-between">

                    <h2 className="text-sm font-semibold pt-4 flex-1">
                      Where should we send your matched offices?
                    </h2>

                  </div>
                </DrawerHeader>
                <Separator className="mb-6" />
                <div className="flex gap-3 mb-3 px-4 md:px-0">
                  <Input placeholder="First Name" name="firstName" value={form.firstName} onChange={updateForm} />
                  <Input placeholder="Last Name" name="lastName" value={form.lastName} onChange={updateForm} />
                </div>
                <div className="px-4 md:px-0">
                  <Input placeholder="Company" name="company" value={form.company} onChange={updateForm} />
                </div>
                <div className="mt-6 flex justify-between p-4 md:p-0">
                  <Button variant="outline" onClick={() => setStep(6)}>
                    Back
                  </Button>
                  <OhButton
                    disabled={!form.lastName || !form.company}
                    onClick={() => setStep(8)}
                  >
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 8: Comments + Phone */}
            {step === 8 && (
              <>
                <DrawerHeader className="px-4">
                  <div className="flex items-center justify-between">

                    <h2 className="text-sm font-semibold pt-4 flex-1">
                      Additional notes or special requirements
                    </h2>

                  </div>
                </DrawerHeader>
                <Separator className="mb-6" />
                <div className="px-4 md:px-0">
                  <Textarea
                    placeholder="Tell us more about your team and office needs."
                    value={payload.comments}
                    onChange={(e) => updatePayload({ comments: e.target.value })}
                    className="border border-[#CACACA]"
                  />
                </div>

                <h2 className="text-lg font-semibold mt-6 mb-4 text-center">And what’s your phone number?</h2>
                <div className="px-4 md:px-0">
                  <PhoneInput
                    country={phoneDefaultCountry}
                    value={payload.phone}
                    onChange={(phone, data, _event, formattedValue) => {
                      const normalized = normalizePhone(phone, formattedValue);
                      updatePayload({
                        phone: normalized,
                        phoneCountry: (data as any)?.name || payload.phoneCountry,
                      });
                    }}
                    inputStyle={{ width: "100%", paddingLeft: 60, borderColor: "#e6e6e6" }}
                    buttonStyle={{ width: 50, borderColor: "#e6e6e6" }}
                    prefix="+"
                  />
                </div>

                <div className="mt-6 flex justify-between p-4 md:p-0">
                  <Button variant="outline" onClick={() => setStep(7)}>
                    Back
                  </Button>
                  <OhButton
                    disabled={!payload.phone}
                    onClick={() => {
                      postPayload();
                      // markCompleted();
                    }}
                  >
                    Submit
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 9: Confirmation */}
            {step === 9 && (
              <div className="p-6 text-center">
                <p className="text-gray-700"
                  id="unique-visitor-modal-heading"
                  data-testid="unique-visitor-modal-heading">
                  Good news, your search is underway and our team is looking for options.
                </p>
                <p className="mt-4 text-gray-700">We have over</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{officeCount} Offices Available</p>
                <OhButton
                  className="mt-6"
                  onClick={handleSubmit}
                  asChild
                >
                  <Link
                    to={convertSearchUrlToLocationPage(payload.geoSearch)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Explore Now
                  </Link>
                </OhButton>
              </div>
            )}
          </DrawerContent>
        </Drawer>

      ) : (
        <Dialog
          open={visible}
          onOpenChange={handleModalClose}
        >

          <DialogContent className={`max-w-xl w-full transition-all duration-200 ${step === 5
            ? "max-h-[45vh] overflow-visible"
            : "max-h-[95vh] overflow-auto"
            } `}>
            <div className="absolute top-0  w-full bg-gray-200 rounded-full h-3 z-50">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${step === 9 ? "bg-oh-primary" : "bg-oh-primary"
                  }`}
                style={{ width: `${step === 9 ? 100 : (step / 8) * 100}%` }}
              />
            </div>
            {/* Step 1: Office Size (FIXED) */}
            {step === 1 && (
              <>
                <p className="text-lg font-semibold mb-4 text-center">
                  How many people do you need office space for?
                </p>

                <RadioGroup
                  value={payload.peopleCount} // use label as stable key
                  onValueChange={(label) => {
                    const chosen = officeSizeOptions.find((o) => o.label === label)!;
                    updatePayload({
                      peopleCount: chosen.label,
                      minDesk: chosen.minDesk,
                      maxDesk: chosen.maxDesk,
                    });
                    setStep(2);
                  }}
                  onClick={() => {
                    fireConversionEvent(DATA_LAYER_EVENTS.QUOTE_REQUEST_MODAL_CONTINUE);

                  }}
                  className="border rounded-lg gap-0"
                >
                  {officeSizeOptions.map((option) => (
                    <div
                      key={option.label}
                      className={`flex border border-[#eee] items-center space-x-2 px-4 py-4 ${payload.peopleCount === option.label ? "bg-gray-100" : ""
                        }`}
                    >
                      <RadioGroupItem value={option.label} id={option.label} />
                      <Label htmlFor={option.label}>{option.label}</Label>
                    </div>
                  ))}
                </RadioGroup>

                <div className="mt-6 flex justify-end">
                  <OhButton
                    disabled={!payload.peopleCount}
                    onClick={() => {
                      fireConversionEvent(DATA_LAYER_EVENTS.QUOTE_REQUEST_MODAL_CONTINUE);
                      setStep(2);
                    }}
                  >
                    Next
                  </OhButton>
                </div>
              </>

            )}

            {/* Step 2: Duration */}
            {step === 2 && (
              <>
                <h2 className="text-lg font-semibold mb-4 text-center">
                  How long are you looking to rent for?
                </h2>
                <OptionRadioGroup
                  value={payload.duration}
                  options={durationOptions}
                  onChange={(val) => {
                    updatePayload({ duration: val });
                    setStep(3);
                  }}
                />
                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <OhButton onClick={() => setStep(3)}>
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 3: Budget */}
            {step === 3 && (
              <>
                <h2 className="text-lg font-semibold mb-4 text-center">What’s your budget?</h2>
                <div className="relative flex">
                  <Select
                    value={payload.budgetCurrency}
                    onValueChange={(val) => updatePayload({ budgetCurrency: val })}
                  >
                    <SelectTrigger className="w-28 rounded-tr-none rounded-br-none bg-gray-200 focus:ring-transparent">
                      <SelectValue placeholder={payload.budgetCurrency} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(regionConfigs)
                        .filter(([_, cfg]: [string, any]) => (cfg as any).top || cfg.region === region)
                        .sort(([_, a]: any, [__, b]: any) => {
                          if (a.region === region) return -1;
                          if (b.region === region) return 1;
                          return 0;
                        })
                        .map(([key, cfg]: [string, any]) => (
                          <SelectItem key={key} value={cfg.currency} className="pl-2 pr-8 [&>span]:left-auto [&>span]:right-2">
                            <div className="flex items-center gap-2">
                              <img
                                src={getFlagUrl(cfg.region)}
                                alt={cfg.country}
                                className="w-5 h-5 object-cover rounded-sm shrink-0"
                              />
                              <span>{cfg.currency}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="text" // use text to show formatted value
                    placeholder="Please enter your monthly budget"
                    className="rounded-tl-none rounded-bl-none"
                    value={formatBudget(payload.budget)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      const num = Number(raw);
                      if (!isNaN(num)) {
                        updatePayload({ budget: num });
                      }
                    }}
                  />
                </div>
                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <OhButton disabled={!payload.budget || payload.budget <= 0} onClick={() => setStep(4)}>
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 4: Move-in */}
            {step === 4 && (
              <>
                <h2 className="text-lg font-semibold mb-4 text-center">
                  How soon are you looking to move in?
                </h2>
                <OptionRadioGroup
                  value={payload.moveSchedule}
                  options={moveinOptions}
                  onChange={(val) => {
                    updatePayload({ moveSchedule: val });
                    setStep(5);
                  }}
                />
                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(3)}>
                    Back
                  </Button>
                  <OhButton onClick={() => setStep(5)}>
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 5: Location */}
            {step === 5 && (
              <div className="flex flex-col">
                <h2 className="text-lg font-semibold mb-4 text-center">
                  Where do you need Office Space?
                </h2>
                <div className={`overflow-visible flex-1 transition-all duration-200 ${showingSuggestions ? "max-h-[300px]" : ""
                  }`}>
                  <AutoPlaces
                    inModal={true}
                    value={payload.location}
                    onSuggestionsVisibilityChange={setShowingSuggestions}
                    onLocationSelect={async (val: string, location: string) => {
                      if (!val || !location) {
                        updatePayload({
                          geoSearch: "",
                          location: "",
                          locationCountry: "",
                          locationState: "",
                          locationStreet: "",
                          locationCity: "",
                          locationLatLng: { lat: undefined, lng: undefined }
                        });
                        return;
                      }

                      const data: any = { geoSearch: "", locationCountry: "", locationState: "", locationStreet: "" };
                      const [base, query] = val.split("?");
                      // Only the min-desk (minPeople) filter carries over to the
                      // location page URL — budget/other filters are left off.
                      const withFilters = val.includes("?")
                        ? `${val}&minPeople=${payload.minDesk}`
                        : `${val}?minPeople=${payload.minDesk}`;
                      data.geoSearch = convertSearchUrlToLocationPage(withFilters);

                      const segments = base.split("/").filter(Boolean);
                      const regionCode = segments[0] === "search" ? "us" : segments[0];
                      const locationSegments =
                        segments[0] === "search" ? segments.slice(1) : segments.slice(1);

                      // Every pick (country / state / city+) activates Next: geoSearch
                      // is already set above, and coord-less picks get their lat/lng
                      // from the geocode fallback below. URLSearchParams safely parses
                      // lat/lng when the URL carries a query string (guarded so an
                      // undefined query never crashes).
                      const partsQ = new URLSearchParams(query || "");
                      let lat: any = partsQ.get("latitude") || undefined;
                      let lng: any = partsQ.get("longitude") || undefined;

                      const config = getConfig(regionCode as any) as any;
                      data.locationCountry = config?.country || "";
                      data.region = regionCode;
                      if (
                        locationSegments.length >= 2 &&
                        !locationSegments[0]?.startsWith("office-space")
                      ) {
                        data.locationCity = locationSegments[locationSegments.length - 1];
                      }

                      try {
                        // Some pages don't preload Google Maps JS — load it before
                        // geocoding, otherwise geocodeByAddress throws "Cannot read
                        // properties of undefined (reading 'maps')". Resolves
                        // immediately when Maps is already present; skips cleanly
                        // if it can't load (Next still works via geoSearch, and the
                        // count API falls back to country/state/city).
                        const mapsReady = await new Promise<boolean>((resolve) =>
                          loadGoogleMapsScript(resolve),
                        );
                        if (mapsReady && (window as any).google?.maps) {
                          const geocodeResults = await geocodeByAddress(location);
                          const { state_long, state_short, streetName, city, country } = getLocationVars(geocodeResults as any);

                          data.locationState = state_long || state_short || "";
                          data.locationStreet = streetName || "";
                          if (!data.locationCity && city) data.locationCity = city;
                          if (!data.locationCountry && country) data.locationCountry = country;

                          // An area/city pick's URL carries no coords, but the
                          // submit's available-listings-count API requires
                          // lat/lng — fall back to the geocode result's point.
                          const point = (geocodeResults as any)?.[0]?.geometry?.location;
                          if ((lat == null || lng == null) && point) {
                            lat = typeof point.lat === "function" ? point.lat() : point.lat;
                            lng = typeof point.lng === "function" ? point.lng() : point.lng;
                          }
                        }
                      } catch (err) {
                        console.error("Error resolving location details", err);
                      }

                      // Only STREET/CITY picks carry a meaningful point. A
                      // COUNTRY/STATE pick's geocode returns the region centroid
                      // (e.g. Australia's desert middle), so a radius count around
                      // it returns 0 — omit lat/lng for those and let the submit's
                      // count API fall back to country/state/city.
                      const isCityOrStreet = !!data.locationCity || !!data.locationStreet;
                      updatePayload({
                        ...data,
                        location,
                        locationLatLng: isCityOrStreet
                          ? { lat, lng }
                          : { lat: undefined, lng: undefined },
                      });
                    }}
                  />
                </div>
                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(4)}>
                    Back
                  </Button>
                  <OhButton disabled={!payload.geoSearch} onClick={() => setStep(6)}>
                    Next
                  </OhButton>
                </div>
              </div>
            )}

            {/* Step 6: Email */}
            {step === 6 && (
              <>
                <h2 className="text-lg font-semibold mb-4 text-center">
                  What email address should we send quotes to?
                </h2>
                <div>
                  <Input
                    placeholder="Please enter your email"
                    name="email"
                    value={form.email}
                    onChange={updateForm}
                  />
                  {emailError && (
                    <p className="text-red-500 text-sm mt-1">{emailError}</p>
                  )}
                  {emailFreemailHint && (
                    <div className="mt-1 flex items-start gap-1.5 text-sm text-green-600">
                      <Lightbulb
                        className="mt-0.5 h-4 w-4 shrink-0 fill-yellow-400 stroke-black stroke-2"
                        aria-hidden
                      />
                      <span>{emailFreemailHint}</span>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(5)}>
                    Back
                  </Button>
                  <OhButton
                    disabled={
                      !form.email || !isEmailValid || isEmailChecking
                    }
                    onClick={handleEmailStepNext}
                  >
                    {isEmailChecking ? "Checking..." : "Next"}
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 7: Name + Company */}
            {step === 7 && (
              <>
                <h2 className="text-lg font-semibold mb-4 text-center">
                  Where should we send your matched offices?
                </h2>
                <div className="flex gap-3 mb-3">
                  <Input placeholder="First Name" name="firstName" value={form.firstName} onChange={updateForm} />
                  <Input placeholder="Last Name" name="lastName" value={form.lastName} onChange={updateForm} />
                </div>
                <Input placeholder="Company" name="company" value={form.company} onChange={updateForm} />
                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(6)}>
                    Back
                  </Button>
                  <OhButton
                    disabled={!form.lastName || !form.company}
                    onClick={() => setStep(8)}
                  >
                    Next
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 8: Comments + Phone */}
            {step === 8 && (
              <>
                <h2 className="text-lg font-semibold mb-4 text-center">Additional notes or special requirements</h2>
                <Textarea
                  placeholder="Tell us more about your team and office needs."
                  value={payload.comments}
                  onChange={(e) => updatePayload({ comments: e.target.value })}
                />
                <h2 className="text-lg font-semibold mt-6 mb-4 text-center">And what’s your phone number?</h2>
                <PhoneInput
                  country={phoneDefaultCountry}
                  value={payload.phone.replace(/^\+/, "")}
                  onChange={(phone, _data, _event, formattedValue) => {
                    const normalized = normalizePhone(phone, formattedValue);
                    updatePayload({
                      phone: normalized,
                      phoneCountry: (_data as any)?.name || payload.phoneCountry,
                    });
                  }}
                  inputStyle={{ width: "100%", paddingLeft: 60, borderColor: "#e6e6e6" }}
                  buttonStyle={{ width: 50, borderColor: "#e6e6e6" }}
                  prefix="+"
                />
                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(7)}>
                    Back
                  </Button>
                  <OhButton
                    disabled={!payload.phone}
                    onClick={() => {
                      postPayload();
                      // markCompleted();
                    }}
                  >
                    Submit
                  </OhButton>
                </div>
              </>
            )}

            {/* Step 9: Confirmation */}
            {step === 9 && (
              <div className="p-6 text-center">
                <p className="text-gray-700" id="unique-visitor-modal-heading" data-testid="unique-visitor-modal-heading">
                  Good news, your search is underway and our team is looking for options.
                </p>
                <p className="mt-4 text-gray-700">We have over</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{officeCount} Offices Available</p>
                <OhButton className="mt-6" onClick={handleSubmit} asChild>
                  <Link to={convertSearchUrlToLocationPage(payload.geoSearch)} target="_blank" rel="noopener noreferrer">
                    Explore Now
                  </Link>
                </OhButton>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
